import { type FC, useRef, useEffect, useState, useMemo } from "react";

import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";

import MainLayout from "@/pages/layouts/MainLayout";
import { PageActions } from "@/pages/layouts/components/PageActions";
import { useUser, useAppContext } from "@/shared/hooks/useAppContext";
import { useUserCustomizations } from "@/shared/hooks/useUserCustomizations";
import { useUserEstateRole } from "@/shared/hooks/useUserEstateRole";
import { useAuthorityEligibility } from "@/shared/hooks/useAuthorityEligibility";
import { useIntakeStatus } from "@/shared/hooks/useIntakeStatus";
import { useMarkIntakeStep } from "@/shared/hooks/useMarkIntakeStep";
import { INTAKE_TASK_IDS } from "@/shared/constants/taskIds";
import { useHasDeathCert } from "@/shared/hooks/useHasDeathCert";
import { useUserContacts } from "@/shared/hooks/useUserContacts";
import { useKeyDocCount } from "@/shared/hooks/useKeyDocCount";

import { useAuthorityAnswers, getAuthorityBucket } from "@/shared/hooks/useAuthorityAnswers";
import { useModuleProgress } from "@/shared/hooks/useModuleProgress";
import { ASSET_SEARCH_TASK_ID, isAssetSearchComplete } from "@/shared/constants/assetSearch";
import { POST_LOSS_ROUTES } from "@/shared/constants/paths";
import { useAuthorityContext } from "@/shared/hooks/useAuthorityContext";
import { EventType, trackEvent } from "@/shared/utils/core";


import { resolveHomeState } from "./resolveHomeState";
import { HomeView } from "./components/HomeView";
import { TriageModal } from "./components/TriageModal";
import { LegalAuthorityModal } from "./components/LegalAuthorityModal";

type AuthorityCtaDestination =
  | "paywall"
  | "get_authorized"
  | "legal_authority_modal"
  | "find_assets"
  | "documents";

export const OverviewPage: FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const user = useUser();
  const {
    getCustomization,
    setCustomization,
    isFetched: isCustomizationsFetched,
  } = useUserCustomizations();
  const intakeStatus = useIntakeStatus();
  const markIntakeStep = useMarkIntakeStep();
  const hasCompletedTriageModal = intakeStatus.hasCompletedIntro;
  const hasDismissedTriageModal = !!getCustomization<boolean>("dismissed_welcome_modal", false);
  const showTriageModalOnLoad =
    isCustomizationsFetched &&
    intakeStatus.isFetched &&
    !hasCompletedTriageModal &&
    !hasDismissedTriageModal;
  const [forceShowIntake, setForceShowIntake] = useState(false);
  const [localDismissed, setLocalDismissed] = useState(false);
  // Stays open after hasCompletedIntro flips mid-triage, until onComplete/onDismiss
  const [autoOpened, setAutoOpened] = useState(false);
  if (showTriageModalOnLoad && !autoOpened) setAutoOpened(true);
  const showTriageModal = (autoOpened || forceShowIntake) && !localDismissed;

  const setCustomizationRef = useRef(setCustomization);
  setCustomizationRef.current = setCustomization;
  const readyRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      readyRef.current = true;
    }, 100);
    return () => {
      clearTimeout(timer);
      if (readyRef.current) {
        setCustomizationRef.current("overview_visited", true);
      }
    };
  }, []);

  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const { path: authorityPath } = useAuthorityContext(estateId);
  const { legalRole, hasLegalAuthority, isLoading: isRoleLoading } = useUserEstateRole();
  const isEligibleForAuthority = useAuthorityEligibility();

  const [searchParams, setSearchParams] = useSearchParams();
  const modalParam = searchParams.get("modal");
  const showLegalAuthorityModal =
    isEligibleForAuthority && modalParam === "legal-authority" && !showTriageModal;

  const clearModalParam = () => {
    if (modalParam) {
      const next = new URLSearchParams(searchParams);
      next.delete("modal");
      setSearchParams(next, { replace: true });
    }
  };

  const { hasDeathCert } = useHasDeathCert(estateId);

  const { contacts } = useUserContacts();
  const contactCount = contacts?.length ?? 0;
  const { keyDocCount, keyDocTotal } = useKeyDocCount(estateId);

  const { savedAnswers: authorityAnswers, isFetched: isAuthorityFetched } =
    useAuthorityAnswers(estateId);
  const hasCompletedAuthorityModal = intakeStatus.hasCompletedLegalAuthority;

  const firstName = user?.first_name || "";
  const personName = person?.first_name || "your loved one";
  const hasInitialInfo = !!person?.full_name;
  const hasLegalRoleSet = !!legalRole && legalRole !== "none";

  const someoneElseHasAuthority =
    authorityAnswers?.appointment === "someone-else" ||
    authorityAnswers?.trust === "someone-else-trustee";
  const nomineeName =
    authorityAnswers?.appointment_name || authorityAnswers?.trust_name || undefined;

  const isHomeLoading =
    !isCustomizationsFetched ||
    !intakeStatus.isFetched ||
    isRoleLoading ||
    (!!estateId && !isAuthorityFetched);

  const hasCompletedIntake = hasCompletedTriageModal;

  const { taskStatusMap } = useModuleProgress();
  const searchComplete = isAssetSearchComplete(taskStatusMap.get(ASSET_SEARCH_TASK_ID));

  const hasEstablishedAuthority = hasLegalAuthority || intakeStatus.hasAuthorityDocument;

  const homeConfig = useMemo(
    () =>
      resolveHomeState({
        hasCompletedIntake,
        hasLegalAuthority: hasEstablishedAuthority,
        someoneElseHasAuthority: !!someoneElseHasAuthority,
        isAssetSearchComplete: searchComplete,
      }),
    [
      hasCompletedIntake,
      hasEstablishedAuthority,
      someoneElseHasAuthority,
      searchComplete,
      hasCompletedAuthorityModal,
    ],
  );

  const handleCompleteIntake = () => {
    setForceShowIntake(false);
    setLocalDismissed(true);
    queryClient.invalidateQueries({ queryKey: ["intakeStatus"] });
    queryClient.invalidateQueries({ queryKey: ["userEstateRole"] });
    queryClient.invalidateQueries({ queryKey: ["hasDeathCert"] });
    queryClient.invalidateQueries({ queryKey: ["keyDocCount"] });
    queryClient.invalidateQueries({ queryKey: ["authorityAnswers"] });
  };

  const handleDismissIntake = () => {
    setForceShowIntake(false);
    setLocalDismissed(true);
    setCustomization("dismissed_welcome_modal", true);
  };

  const trackAuthorityCta = (destination: AuthorityCtaDestination) =>
    trackEvent(EventType.AUTHORITY_CTA_CLICKED, {
      destination,
      home_state: homeConfig.state,
      authority_bucket: getAuthorityBucket(authorityAnswers) ?? null,
      estate_id: estateId != null ? String(estateId) : null,
    });

  const handleAuthorityCtaClick = () => {
    if (homeConfig.state === "no-authority") {
      if (hasCompletedAuthorityModal) {
        trackAuthorityCta("get_authorized");
        navigate(POST_LOSS_ROUTES.GET_AUTHORIZED);
      } else {
        trackAuthorityCta("legal_authority_modal");
        const next = new URLSearchParams(searchParams);
        next.set("modal", "legal-authority");
        setSearchParams(next);
      }
    } else if (homeConfig.state === "has-authority" || homeConfig.state === "settle-estate") {
      trackAuthorityCta("find_assets");
      navigate(POST_LOSS_ROUTES.FIND_ASSETS);
    } else if (homeConfig.state === "someone-else-authority") {
      trackAuthorityCta("documents");
      navigate(POST_LOSS_ROUTES.DOCUMENTS);
    }
  };

  return (
    <MainLayout actions={<PageActions />}>
      <div className="flex flex-col gap-10 w-full">
        {isHomeLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="size-8 animate-spin text-brand" />
          </div>
        ) : (
          <HomeView
            config={homeConfig}
            personName={personName}
            nomineeName={nomineeName}
            legalRole={legalRole}
            hasInitialInfo={hasInitialInfo}
            hasDeathCert={!!hasDeathCert}
            hasLegalRole={hasLegalRoleSet}
            authorityPath={hasCompletedAuthorityModal ? authorityPath : null}
            hasCompletedAuthorityModal={hasCompletedAuthorityModal}
            contactCount={contactCount}
            keyDocCount={keyDocCount}
            keyDocTotal={keyDocTotal}
            isJourneyPaused={showTriageModal || showLegalAuthorityModal}
            onFinishSetup={() => {
              setLocalDismissed(false);
              setForceShowIntake(true);
            }}
            onAuthorityCtaClick={handleAuthorityCtaClick}
          />
        )}
      </div>
      <TriageModal
        open={showTriageModal}
        openSource={forceShowIntake ? "finish_setup" : "auto"}
        firstName={firstName}
        personFirstName={person?.first_name || ""}
        onComplete={handleCompleteIntake}
        onDismiss={handleDismissIntake}
      />
      <LegalAuthorityModal
        open={showLegalAuthorityModal}
        personFirstName={person?.first_name || ""}
        estateId={estateId}
        onComplete={() => {
          markIntakeStep.mutate({
            taskId: INTAKE_TASK_IDS.LEGAL_AUTHORITY,
            status: "completed",
          });
          clearModalParam();
          queryClient.invalidateQueries({ queryKey: ["userEstateRole"] });
        }}
        onClose={() => {
          markIntakeStep.mutate({
            taskId: INTAKE_TASK_IDS.LEGAL_AUTHORITY,
            status: "skipped",
            answers: { dismissed: true },
          });
          clearModalParam();
        }}
      />
    </MainLayout>
  );
};
